import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { messageAPI, authAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Send, Loader2, ArrowLeft } from 'lucide-react';

export default function Messages() {
  const { userId } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      if (userId) {
        fetchMessages(userId);
      } else {
        fetchConversations();
      }
    }
  }, [currentUser, userId]);

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      const response = await messageAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (targetUserId) => {
    setIsLoading(true);
    try {
      const response = await messageAPI.getMessages(targetUserId);
      setMessages(response.data);
      
      // Get user info if we have messages
      if (response.data.length > 0) {
        const msg = response.data[0];
        setSelectedUser({
          user_id: targetUserId,
          name: msg.sender_id === targetUserId ? msg.sender_name : 'User',
          picture: msg.sender_id === targetUserId ? msg.sender_picture : null
        });
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !userId) return;

    setIsSending(true);
    try {
      const response = await messageAPI.send({
        recipient_id: userId,
        content: newMessage
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  // Conversation view (when a userId is selected)
  if (userId) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="messages-conversation">
        <Navbar />
        
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Link to="/messages">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            {selectedUser && (
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={selectedUser.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700">
                    {selectedUser.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Link to={`/users/${selectedUser.user_id}`}>
                  <span className="font-semibold text-slate-900 hover:text-indigo-600">
                    {selectedUser.name}
                  </span>
                </Link>
              </div>
            )}
          </div>

          {/* Messages */}
          <Card className="mb-4">
            <ScrollArea className="h-[400px] p-4">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => {
                    const isOwn = message.sender_id === currentUser?.user_id;
                    return (
                      <div
                        key={message.message_id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            isOwn 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-slate-100 text-slate-900'
                          }`}
                        >
                          <p>{message.content}</p>
                          <p className={`text-xs mt-1 ${isOwn ? 'text-indigo-200' : 'text-slate-500'}`}>
                            {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </Card>

          {/* Send Message */}
          <form onSubmit={handleSend} className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1"
              data-testid="message-input"
            />
            <Button type="submit" disabled={isSending || !newMessage.trim()} className="bg-indigo-600">
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        </main>
      </div>
    );
  }

  // Conversations list view
  return (
    <div className="min-h-screen bg-slate-50" data-testid="messages-page">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Messages</h1>

        {conversations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No conversations yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Start a conversation by visiting someone's profile
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {conversations.map((conv) => (
              <Link key={conv._id} to={`/messages/${conv._id}`}>
                <Card className="card-hover">
                  <CardContent className="p-4 flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={conv.user?.picture} />
                      <AvatarFallback className="bg-indigo-100 text-indigo-700">
                        {conv.user?.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">{conv.user?.name}</h3>
                        <span className="text-xs text-slate-500">
                          {formatDistanceToNow(new Date(conv.last_message?.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 truncate">
                        {conv.last_message?.content}
                      </p>
                    </div>
                    
                    {conv.unread_count > 0 && (
                      <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-bold">{conv.unread_count}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
